import React, { useState, useEffect } from 'react';
import { useAuth, api, useTenant } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { Database, Activity, Server, Loader2, RefreshCw, DollarSign, Users, Crown, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import TenantFilter from '../components/TenantFilter';

const MonitoringPage = () => {
  const { token } = useAuth();
  const { selectedTenant, getTenantParam } = useTenant();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dados fictícios de licenças e atividade de usuários
  const licensesData = {
    total_active: 12,
    plans: {
      per_mission: 2,
      basic: 4,
      essential: 5,
      pro: 1
    },
    monthly_revenue: 4250.00
  };

  const userActivity = [
    { id: 1, name: 'Admin Master', email: 'admin@prapp.com.br', role: 'mestre', license: 'Ilimitado', last_access: new Date().toISOString(), status: 'online' },
    { id: 2, name: 'João Silva', email: 'joao.silva@transportes.com', role: 'adm', license: 'Pro', last_access: new Date(Date.now() - 1800000).toISOString(), status: 'online' },
    { id: 3, name: 'Maria Santos', email: 'maria@logistica.com', role: 'gestor_operacional', license: 'Essencial', last_access: new Date(Date.now() - 7200000).toISOString(), status: 'away' },
    { id: 4, name: 'Pedro Costa', email: 'pedro@frete.com', role: 'operacional', license: 'Básico', last_access: new Date(Date.now() - 86400000).toISOString(), status: 'offline' },
    { id: 5, name: 'Ana Oliveira', email: 'ana@cargas.com', role: 'financeiro', license: 'Essencial', last_access: new Date(Date.now() - 172800000).toISOString(), status: 'offline' },
    { id: 6, name: 'Carlos Lima', email: 'carlos@express.com', role: 'operacional', license: 'Por Missão', last_access: new Date(Date.now() - 259200000).toISOString(), status: 'offline' },
  ];

  const roleLabels = {
    mestre: 'Master',
    adm: 'Administrador',
    financeiro: 'Financeiro',
    gestor_operacional: 'Gestor Operacional',
    operacional: 'Operacional'
  };

  const formatLastAccess = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 5) return 'Agora';
    if (diffMins < 60) return `Há ${diffMins} min`;
    if (diffHours < 24) return `Há ${diffHours}h`;
    return `Há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
  };

  useEffect(() => {
    fetchStats();
  }, [token, selectedTenant]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/monitoring/stats${getTenantParam()}`, token);
      setStats(data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const dbUsagePercent = Math.min((stats?.database?.missions / 10000) * 100, 100);

  return (
    <div className="space-y-6" data-testid="monitoring-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pl-14">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans' }}>
            Monitoramento
          </h1>
          <p className="text-slate-500 mt-1">Painel técnico do administrador master</p>
        </div>
        <div className="flex items-center gap-2">
          <TenantFilter />
          <Button variant="outline" onClick={fetchStats} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Database Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Missões</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                  {stats?.database?.missions || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Usuários</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                  {stats?.database?.users || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Server className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Fornecedores</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                  {stats?.database?.suppliers || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Database className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Clientes</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                  {stats?.database?.customers || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Database className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Missions Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <Activity className="w-5 h-5 text-orange-500" />
              Missões por Mês (Últimos 6 meses)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.monthly_missions || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0F172A" radius={[4, 4, 0, 0]} name="Missões" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* System Resources */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
              <Server className="w-5 h-5 text-orange-500" />
              Recursos do Sistema
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-slate-600">Uso do Banco de Dados</span>
                <span className="text-sm font-mono text-slate-900">{dbUsagePercent.toFixed(1)}%</span>
              </div>
              <Progress value={dbUsagePercent} className="h-2" />
              <p className="text-xs text-slate-500 mt-1">
                {stats?.estimated_costs?.database_gb || 0.5} GB estimado
              </p>
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-slate-900 mb-3">Status das Integrações</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600">Pagamentos (Stripe)</span>
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-700">Pendente</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Estimated Costs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
            <DollarSign className="w-5 h-5 text-orange-500" />
            Custos Operacionais Estimados (Mensal)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Banco de Dados</p>
              <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                ~R$ 50/mês
              </p>
              <p className="text-xs text-slate-500 mt-1">{stats?.estimated_costs?.database_gb || 0} GB</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">API Calls</p>
              <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                {stats?.estimated_costs?.api_calls || 0}
              </p>
              <p className="text-xs text-slate-500 mt-1">Chamadas/mês</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-md border border-amber-200">
              <p className="text-sm text-amber-700 mb-1">Total Estimado</p>
              <p className="text-xl font-bold text-amber-900" style={{ fontFamily: 'JetBrains Mono' }}>
                R$ {(stats?.estimated_costs?.total_brl || 0).toFixed(2)}
              </p>
              <p className="text-xs text-amber-600 mt-1">Custo operacional</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Licenses Revenue Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
            <Crown className="w-5 h-5 text-amber-500" />
            Faturamento de Licenças
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200">
              <p className="text-sm text-amber-700 mb-1">Receita Mensal</p>
              <p className="text-2xl font-bold text-amber-900" style={{ fontFamily: 'JetBrains Mono' }}>
                R$ {licensesData.monthly_revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Por Missão</p>
              <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                {licensesData.plans.per_mission}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Básico</p>
              <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                {licensesData.plans.basic}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Essencial</p>
              <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                {licensesData.plans.essential}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Pro</p>
              <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                {licensesData.plans.pro}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Total de licenças ativas</span>
            <Badge className="bg-green-100 text-green-700 text-lg px-3">
              {licensesData.total_active}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* User Activity Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
            <Users className="w-5 h-5 text-blue-500" />
            Atividade de Usuários
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Usuário</th>
                  <th>Email</th>
                  <th>Perfil</th>
                  <th>Licença</th>
                  <th>Último Acesso</th>
                </tr>
              </thead>
              <tbody>
                {userActivity.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${
                          user.status === 'online' ? 'bg-green-500' : 
                          user.status === 'away' ? 'bg-amber-500' : 'bg-slate-300'
                        }`} />
                        <span className="text-xs text-slate-500 capitalize">{user.status}</span>
                      </div>
                    </td>
                    <td className="font-medium text-slate-900">{user.name}</td>
                    <td className="text-slate-500 text-sm">{user.email}</td>
                    <td>
                      <Badge className="bg-slate-100 text-slate-600">
                        <Shield className="w-3 h-3 mr-1" />
                        {roleLabels[user.role] || user.role}
                      </Badge>
                    </td>
                    <td>
                      <Badge className={
                        user.license === 'Ilimitado' ? 'bg-amber-100 text-amber-700' :
                        user.license === 'Pro' ? 'bg-purple-100 text-purple-700' :
                        user.license === 'Essencial' ? 'bg-blue-100 text-blue-700' :
                        user.license === 'Básico' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-600'
                      }>
                        <Crown className="w-3 h-3 mr-1" />
                        {user.license}
                      </Badge>
                    </td>
                    <td>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span className={user.status === 'online' ? 'text-green-600 font-medium' : 'text-slate-500'}>
                          {formatLastAccess(user.last_access)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default MonitoringPage;
